import React from "react";
import Cookies from "js-cookie";
import { ACCESS } from "@/utils/enviroments";
import { useRouter } from "next/router";
import { Api } from "@/services/configs/axios";
import { DashboardService } from "@/services/endpoints/login";
import { LoginApi } from "@/services/configs/axios";
import { AddSvgToData } from "@/utils/SvgDictionaries/addSvgToData";
import { safePush } from "@/utils/safePush";

export interface IAuthContextProps {
  role: string;
  name: string;
  email: string;
  lastFive: any[];
  password: string;
  loginError: boolean;
  emailError: boolean;
  isDashboard: boolean;
  userIsAdmin: boolean;
  passwordError: boolean;
  authenticated: boolean;
  dashboardAuth: boolean;
  handleLogin: () => Promise<string | null>;
  handleLogout: () => void;
  emailErrorMessage: string;
  setEmail: (e: string) => void;
  setPassword: (e: string) => void;
  setEmailError: (e: boolean) => void;
  handleLoginDashboard: (e: any) => void;
  setLoginError: React.Dispatch<React.SetStateAction<boolean>>;
  setPasswordError: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AuthContext = React.createContext({} as IAuthContextProps);

interface IProps {
  children: React.ReactNode;
}

export const AuthContextProvider: React.FC<IProps> = ({ children }) => {
  const router = useRouter();
  const pathname = router.pathname;
  const push = router.push;
  const isReady = router.isReady;
  const [name, setName] = React.useState("");
  const [role, setRole] = React.useState('');
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const { LoginCerberusDashboard } = new DashboardService();
  const [emailError, setEmailError] = React.useState(false);
  const [loginError, setLoginError] = React.useState(false);
  const [isDashboard, setIsDashboard] = React.useState(false);
  const [userIsAdmin, setUserIsAdmin] = React.useState(false);
  const [dashboardAuth, setDashboardAuth] = React.useState(false);
  const [passwordError, setPasswordError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState("");
  const [authenticated, setAuthenticated] = React.useState<boolean>(false);

  const handleLogin = async (): Promise<string | null> => {
    const data = { email: ACCESS.EMAIL, password: ACCESS.PASSWORD };
    try {
      const response = await LoginApi.post(`user/login`, data);
      const token = response?.data?.token as string | undefined;
      if (!token) return null;

      Cookies.remove("TOKEN_JWT");
      setAuthenticated(true);
      Cookies.set("TOKEN_JWT", token);
      return token;
    } catch (e) {
      console.log("error", { e });
      return null;
    }
  };

  const handleRole = (roles: any[]) => {
    if (roles.includes("ADMINISTRADORES") || roles.includes("ADMINISTRADOR")) {
      return 'Administrador'
    }

    if (roles.includes('COLABORADORES') || roles.includes("COLABORADOR")) {
      return 'Colaborador'
    }

    if (roles.includes('EDITORES') || roles.includes("EDITOR")) {
      return 'Editor'
    }

    return '';
  }


  const handleLoginDashboard = (e: any) => {
    e.preventDefault();
    setEmailError(false);
    setDashboardAuth(false);
    localStorage.removeItem("DASHBOARD_JWT");
    if (password.length === 0) {
      setPasswordError(true);
      return;
    }

    if (email.length === 0) {
      setEmailError(true);
      return;
    }

    LoginCerberusDashboard({ email, password })
      .then((res) => {
        const { token, info } = res.data as any;

        localStorage.setItem("DASHBOARD_JWT", JSON.stringify(token));

        setDashboardAuth(true);
        console.log('info', info)
        const role = handleRole(info.perfis);
        const name = info.usuario.nome.split(" ")[0] as string;
        const parseName = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();

        localStorage.setItem("DASHBOARD_NAME", JSON.stringify(parseName));
        localStorage.setItem("DASHBOARD_ROLE", JSON.stringify(role));

        if ([...info?.perfis].includes("ADMINISTRADOR") || [...info?.perfis].includes("ADMINISTRADORES")) {
          localStorage.setItem("DASHBOARD_ADMIN", JSON.stringify(true));
          setUserIsAdmin(true);
        }

        setName(parseName);
        setRole(role)

        router.push("/dashboard");
      })
      .catch((e) => {
        setEmailErrorMessage(e.response.data.message);
        setEmailError(true);
      });
  };

  const handleLogout = () => {
    localStorage.removeItem("DASHBOARD_JWT");
    localStorage.removeItem("DASHBOARD_NAME");
    localStorage.removeItem("DASHBOARD_ADMIN");
    setDashboardAuth(false);
    setUserIsAdmin(false);
    if (pathname === "/dashboard/monitoramento/[id]") {
      safePush(router, "/");
      return;
    }
    router.push("/");
  };

  const [lastFive, setLastFive] = React.useState<any[]>([]);

  React.useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = await handleLogin();
        if (!token) return;
        const response = await Api.get('plano/latest', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const { data } = response.data as any;
        const newArray = AddSvgToData(data);
        setLastFive(newArray);
      } catch (err) {
        console.error("Erro durante a inicialização da autenticação:", err);
      }
    };

    if (!isReady) return;

    const shouldInitializeAuth =
      pathname === "/" || pathname.split("/").includes("dashboard");
    if (!shouldInitializeAuth) return;

    initializeAuth();
  }, [isReady, pathname]);


  React.useEffect(() => {
    if (pathname.split("/").includes('dashboard')) {
      setIsDashboard(true);
    } else {
      setIsDashboard(false);
    }
  }, [pathname]);

  React.useEffect(() => {
    const storedToken = localStorage.getItem("DASHBOARD_JWT");
    const jwt = storedToken ? JSON.parse(storedToken) : null;

    const storedIsAdmin = localStorage.getItem("DASHBOARD_ADMIN");
    setUserIsAdmin(storedIsAdmin ? JSON.parse(storedIsAdmin) : false);


    if (jwt) {
      setDashboardAuth(true);
      setName(JSON.parse(localStorage.getItem("DASHBOARD_NAME") as string));
      setRole(JSON.parse(localStorage.getItem("DASHBOARD_ROLE") as string));

      pathname.split("/").find((item) => item !== "dashboard") && push("/dashboard");
    }

    if (!jwt) {
      setDashboardAuth(false);
      const storedIsAdmin = localStorage.getItem("DASHBOARD_ADMIN");
      setUserIsAdmin(storedIsAdmin ? JSON.parse(storedIsAdmin) : false);
      pathname.split("/").find((item) => item === "dashboard") && push("/");
    }
  }, [pathname, push]);

  const value = {
    role,
    name,
    email,
    setEmail,
    password,
    loginError,
    emailError,
    setPassword,
    userIsAdmin,
    handleLogin,
    isDashboard,
    handleLogout,
    passwordError,
    dashboardAuth,
    lastFive,
    setEmailError,
    authenticated,
    setLoginError,
    setPasswordError,
    emailErrorMessage,
    handleLoginDashboard,
  } as IAuthContextProps;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
